import { useLayoutEffect, useRef, useState } from 'react';

/**
 * NAT-2584 — an element's rendered width in CSS pixels, kept current as it resizes; null until
 * measured, and wherever `ResizeObserver` does not exist (jsdom), so a caller always has a
 * width-free fallback.
 */
export function useElementWidth<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const read = () => {
      const next = Math.round(element.getBoundingClientRect().width);
      setWidth((prev) => (next > 0 && next !== prev ? next : prev));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}
